<template>
  <div class="min-h-screen bg-gray-50 px-4 pb-20 ">
    <!-- Toast Notification -->
    <Toast
      :visible="showToast"
      :title="toastTitle"
      :message="toastMessage"
      :variant="toastVariant"
      @close="showToast = false"
    />

    <Modal
      :is-open="unavailableItemsModalOpen"
      size="md"
      :close-on-backdrop="false"
      :show-close-button="false"
      container-class="flex flex-col items-center text-center"
      :scrollable="false"
    >
      <div class="flex flex-col items-center text-center justify-center">
        <div
          class="w-16 h-16 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center mb-6"
        >
          <AlertTriangle :size="32" />
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-2">
          Some Items Are Unavailable
        </h3>
        <p class="text-gray-600 mb-4 leading-relaxed">
          The following items are no longer available:
        </p>
        <ul class="w-full bg-gray-50 rounded-2xl p-4 mb-8 text-left">
          <li
            v-for="name in unavailableItemNames"
            :key="name"
            class="font-semibold text-gray-900 py-1"
          >
            {{ name }}
          </li>
        </ul>
        <div class="w-full space-y-4">
          <button
            class="w-full bg-primary-600 text-white font-bold py-3 rounded-xl shadow-lg shadow-primary-600/20 hover:bg-primary-700 transition-all active:scale-[0.98]"
            @click="removeUnavailableItemsAndPlaceOrder"
          >
            Remove Items and Place Order
          </button>
          <button
            class="w-full text-gray-600 font-bold py-3 rounded-xl hover:bg-gray-100 transition-all"
            @click="goBackToShoppingFromUnavailableModal"
          >
            Go Back to Shopping
          </button>
        </div>
      </div>
    </Modal>

    <Modal
      :is-open="stockLimitModalOpen"
      size="md"
      :close-on-backdrop="false"
      :show-close-button="false"
      container-class="flex flex-col items-center text-center"
      :scrollable="false"
    >
      <div class="flex flex-col items-center text-center justify-center">
        <div
          class="w-16 h-16 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center mb-6"
        >
          <AlertTriangle :size="32" />
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-2">
          Some Quantities Changed
        </h3>
        <p class="text-gray-600 mb-4 leading-relaxed">
          There is not enough stock for the following items:
        </p>
        <ul class="w-full bg-gray-50 rounded-2xl p-4 mb-8 text-left space-y-3">
          <li
            v-for="item in stockLimitAdjustments"
            :key="item.product"
            class="text-gray-900"
          >
            <p class="font-semibold">{{ item.name }}</p>
            <p class="text-sm text-gray-600">
              Requested {{ formatQuantity(item.requested_quantity) }}, only
              {{ formatQuantity(item.available_quantity) }} available.
              {{ formatQuantity(item.removed_quantity) }} will be removed from
              your cart.
            </p>
          </li>
        </ul>
        <div class="w-full space-y-4">
          <button
            class="w-full bg-primary-600 text-white font-bold py-3 rounded-xl shadow-lg shadow-primary-600/20 hover:bg-primary-700 transition-all active:scale-[0.98]"
            @click="applyStockLimitsAndPlaceOrder"
          >
            Update Cart and Place Order
          </button>
          <button
            class="w-full text-gray-600 font-bold py-3 rounded-xl hover:bg-gray-100 transition-all"
            @click="goBackToShoppingFromStockLimitModal"
          >
            Go Back to Shopping
          </button>
        </div>
      </div>
    </Modal>

    <!-- Success State -->
    <CheckoutSuccessState
      :is-success="isSuccess"
      :first-name="details.firstName"
      :order-config="orderConfig"
      :total="total"
      @back-to-order="handleBack"
    />

    <!-- Main Checkout Form -->
    <div
      v-if="!isSuccess"
      class="checkout-main max-w-2xl mx-auto pt-10 pb-10 px-4 transition-opacity duration-300"
    >
      <!-- Header -->
      <div class="relative flex items-center justify-end mb-10">
        <button
          @click="handleBack"
          class="absolute left-0 flex items-center gap-2 p-3 hover:bg-gray-100 rounded-full transition-colors text-gray-600 border border-transparent hover:border-gray-200"
        >
          <ArrowLeft :size="24" />
          <span class="font-semibold">Continue Ordering</span>
        </button>
        <h1 class="text-4xl font-serif font-bold text-gray-900">Checkout</h1>
      </div>

      <!-- Single Vertical Form Container -->
      <div
        class="bg-white rounded-3xl shadow-xl border border-gray-100"
        style="box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04); overflow: visible;"
      >
        <div class="checkout-grid p-8 md:p-10 space-y-10">
          <!-- Contact Section -->
          <div class="checkout-contact space-y-6">
            <h3
              class="text-xl font-bold text-gray-900 pb-2 border-b border-gray-100"
            >
              Your Details
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="First Name"
                v-model="details.firstName"
                placeholder="e.g. Jane"
                :error="!!errors.firstName"
                :error-message="errors.firstName"
                @input="handleFirstNameInput"
              />
              <Input
                label="Last Name"
                v-model="details.lastName"
                placeholder="e.g. Doe"
                :error="!!errors.lastName"
                :error-message="errors.lastName"
                @input="handleLastNameInput"
              />
              <div class="md:col-span-2">
                <Input
                  label="Email Address"
                  type="email"
                  v-model="details.email"
                  placeholder="jane@example.com"
                  autocomplete="email"
                  :error="!!errors.email"
                  :error-message="errors.email"
                  @input="handleEmailInput"
                />
              </div>
              <div class="md:col-span-2">
                <PhoneInput
                  label="Phone Number"
                  v-model="details.phone"
                  :selected-country="selectedCountry"
                  @update:country="handleCountryChange"
                  placeholder="5551234567"
                  :error="!!errors.phone"
                  :error-message="errors.phone"
                  @input="handlePhoneInput"
                />

                <!-- SMS Consent Checkbox -->
                <div
                  class="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200"
                >
                  <p
                    class="text-sm font-bold text-primary-700 mb-3 flex items-center gap-2"
                  >
                    <MessageSquare
                      :size="16"
                      style="fill: rgb(191, 219, 254); stroke: rgb(29, 78, 216);"
                    />
                    We can't send you order updates via SMS unless you check
                    this box! 👇
                  </p>
                  <label
                    class="flex items-start gap-3 cursor-pointer group relative select-none"
                  >
                    <div class="flex items-center h-5 mt-0.5 relative">
                      <input
                        type="checkbox"
                        v-model="smsConsent"
                        @change="saveSmsConsentToLocalStorage"
                        class="relative h-5 w-5 cursor-pointer appearance-none rounded border-2 border-gray-300 bg-white transition-all hover:border-primary-400 focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-blue-200"
                        :class="
                          smsConsent ? 'border-primary-600 bg-primary-600' : ''
                        "
                      />
                      <Check
                        v-if="smsConsent"
                        :size="14"
                        class="absolute left-0 top-0 right-0 bottom-0 m-auto text-white pointer-events-none"
                        style="stroke-width: 3;"
                      />
                    </div>
                    <span
                      class="text-xs text-gray-500 leading-relaxed group-hover:text-gray-600 transition-colors text-justify"
                      style="font-size: 10px;"
                    >
                      By providing your phone number, you agree to receive SMS
                      on Order Notifications, Account Notifications and Customer
                      Care from Biyo POS. Message frequency may vary. Standard
                      Message and Data Rates may apply. Reply STOP to opt out.
                      Reply HELP for help or reach us at
                      <span class="underline underline-offset-2"
                        >support@biyopos.com</span
                      >. We will not share mobile information with third parties
                      for promotional or marketing purposes. For more
                      information view our
                      <a
                        href="https://biyopos.com/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline underline-offset-2"
                        @click.stop
                        >Privacy Policy</a
                      >.
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Order Info Section -->
          <CheckoutOrderInfoSection
            class="checkout-order"
            :cart-items="cartItems"
            :errors="errors"
            :grand-total="grandTotal"
          />

          <!-- Tip Suggestion Section -->
          <CheckoutTipSection
            v-if="areTipsEnabled"
            class="checkout-tip"
            :subtotal="subtotal"
            :tip-option="tipOption"
            :custom-tip="customTip"
            @update:tipOption="tipOption = $event"
            @update:customTip="customTip = $event"
          />

          <!-- Payment Section -->
          <CheckoutPaymentSection
            ref="paymentSection"
            class="checkout-payment"
            :payment-methods="availablePaymentMethods"
            :selected-payment-type="selectedPaymentType"
            @update:selectedPaymentType="selectedPaymentType = $event"
            :card-details="cardDetails"
            @update:cardDetails="cardDetails = $event"
            :errors="errors"
          />

          <!-- Summary & Action -->
          <div class="checkout-summary pt-6 border-t border-gray-100 space-y-6">
            <div class="flex flex-col gap-3 text-gray-600">
              <div class="flex justify-between">
                <span>
                  Subtotal
                  <span class="text-gray-400">
                    ({{ itemCount }} {{ itemCount === 1 ? "item" : "items" }})
                  </span>
                </span>
                <span class="font-medium text-gray-900">
                  {{ formatPrice(grossSubtotal) }}
                </span>
              </div>
              <template v-if="getAppliedPromotions.length">
                <div
                  v-for="promotion in getAppliedPromotions"
                  :key="`${promotion.id}-${promotion.type}`"
                  class="flex justify-between gap-3 text-green-700 font-semibold"
                >
                  <span class="flex items-center gap-1.5 min-w-0">
                    <Tag :size="14" class="flex-shrink-0" />
                    <span class="truncate">{{ promotion.name }}</span>
                  </span>
                  <span class="flex-shrink-0">
                    &minus;{{ formatPrice(promotion.discount) }}
                  </span>
                </div>
              </template>
              <div
                v-else-if="getDiscountTotal > 0"
                class="flex justify-between text-green-700 font-semibold"
              >
                <span class="flex items-center gap-1.5">
                  <Tag :size="14" />
                  Discount
                </span>
                <span>&minus;{{ formatPrice(getDiscountTotal) }}</span>
              </div>
              <div class="flex justify-between" v-if="tipAmount">
                <span>Tip</span>
                <span class="font-medium text-gray-900">
                  {{ formatPrice(tipAmount) }}
                </span>
              </div>
              <div class="flex justify-between">
                <span>Tax</span>
                <span class="font-medium text-gray-900">
                  {{ formatPrice(getTotalTax) }}
                </span>
              </div>
              <div v-if="convenienceTotal > 0" class="flex justify-between">
                <span>Convenience Fee</span>
                <span class="font-medium text-gray-900">
                  {{ formatPrice(convenienceTotal) }}
                </span>
              </div>
              <div
                class="flex justify-between text-lg font-bold text-gray-900 pt-3 border-t border-gray-100 mt-2"
              >
                <span>Total</span>
                <span>{{ formatPrice(grandTotal) }}</span>
              </div>
            </div>

            <button
              @click="handleSubmit"
              :disabled="isSubmitting"
              :class="[
                'w-full text-white font-bold py-5 rounded-2xl shadow-xl hover:shadow-2xl transition-all transform active:scale-95 flex justify-center items-center gap-2 disabled:cursor-not-allowed',
                !isSubmitting
                  ? 'bg-primary-600 hover:bg-primary-700'
                  : 'bg-gray-300 disabled:shadow-none'
              ]"
              :style="
                !isSubmitting
                  ? 'box-shadow: 0 20px 25px rgba(37, 99, 235, 0.2);'
                  : ''
              "
            >
              <span v-if="isSubmitting" class="flex items-center gap-2">
                <Loader2 :size="24" class="animate-spin" /> Processing Order...
              </span>
              <span class="text-xl" v-if="availablePaymentMethods.length > 0">
                Pay {{ formatPrice(grandTotal) }}
              </span>
              <span v-else class="text-xl">
                Confirm Order & Pay on Receipt
              </span>
            </button>

            <div
              class="flex justify-center items-center gap-2 text-xs text-gray-400 font-medium"
            >
              <ShieldCheck :size="14" />
              <p>Payments are secure and encrypted.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { useCheckout } from "@/composables/useCheckout";
import { useSelectedStore } from "@/composables/useSelectedStore";
import Modal from "@/components/ui/Modal.vue";
import CheckoutSuccessState from "@/components/Checkout/CheckoutSuccessState.vue";
import CheckoutOrderInfoSection from "@/components/Checkout/CheckoutOrderInfoSection/index.vue";
import CheckoutTipSection from "@/components/Checkout/CheckoutTipSection.vue";
import CheckoutPaymentSection from "@/components/Checkout/CheckoutPaymentSection.vue";
import { Input, Toast } from "@/components/ui";
import {
  ArrowLeft,
  MessageSquare,
  Check,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  Tag
} from "lucide-vue";
import {
  saveCustomerInfo,
  loadCustomerInfo,
  loadDeliveryAddress,
  peekIdempotencyKey,
  clearIdempotencyKey
} from "@/services/localStorageService";
import { fetchOrderByIdempotencyKey } from "@/helpers/apiHelper";
import { parsePhone, getCountryByCode, COUNTRIES } from "@/helpers/phoneUtils";
import { resolveStoreAlias } from "@/helpers/urlHelper";

export default {
  name: "Checkout",
  components: {
    CheckoutSuccessState,
    Modal,
    CheckoutOrderInfoSection,
    CheckoutTipSection,
    CheckoutPaymentSection,
    Input,
    PhoneInput: () => import("@/components/ui/PhoneInput.vue"),
    Toast,

    ArrowLeft,
    MessageSquare,
    Check,
    Loader2,
    ShieldCheck,
    AlertTriangle,
    Tag
  },
  data() {
    return {
      details: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "" // This will now store JUST the digits during editing
      },
      selectedCountry: COUNTRIES[0], // Default US
      smsConsent: false,
      cardDetails: {
        name: "",
        number: "",
        expiration: "",
        cvv: ""
      },
      paymentProcessor: null,
      isSubmitting: false,
      isSuccess: false,
      tipOption: 0,
      customTip: "",
      errorMessage: "",
      selectedPaymentType: null,
      showToast: false,
      toastTitle: "",
      toastMessage: "",
      toastVariant: "danger",
      unavailableItemsModalOpen: false,
      unavailableItemNames: [],
      unavailableItemsToRemove: [],
      stockLimitModalOpen: false,
      stockLimitAdjustments: [],
      errors: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        cardDetails: {
          name: "",
          number: "",
          expiration: "",
          cvv: ""
        },
        deliveryAddress: "",
        scheduledDay: "",
        scheduledTime: ""
      }
    };
  },
  created() {
    this._checkoutComposable = useCheckout(this.$store, this.$router, this);
    this._selectedStore = useSelectedStore(this.$store);
  },

  computed: {
    ...mapGetters([
      "orderConfig",
      "subtotal",
      "getTotalTax",
      "getTipAmount",
      "getDiscountTotal",
      "getAppliedPromotions",
      "getStore",
      "cartItems",
      "getDiningOptionName",
      "getDiningOptionObject",
      "getServeAt",
      "getDeliveryAddress",
      "getIsStoreClosed",
      "availablePaymentMethods",
      "getCurrencySymbol",
      "formatPrice",
      "isScheduleModalOpen",
      "isRestaurantClosedModalOpen",
      "availableDiningOptions",
      "dining_option"
    ]),
    getDeliveryAddressWatcher() {
      return this.getDeliveryAddress;
    },
    storeAlias() {
      return resolveStoreAlias(this.$route);
    },
    isStoreClosed() {
      return this.getIsStoreClosed;
    },
    currency() {
      return this.getCurrencySymbol || "$";
    },
    tipAmount() {
      if (!this.areTipsEnabled) {
        return 0;
      }
      if (this.tipOption === "custom") {
        return parseFloat(this.customTip) || 0;
      }
      return this.subtotal * (this.tipOption / 100);
    },
    areTipsEnabled() {
      return this.getStore?.orderLiveSettings?.ooTips !== false;
    },
    currentPaymentMethod() {
      return this._checkoutComposable.getCurrentPaymentMethod(
        this.availablePaymentMethods,
        this.selectedPaymentType
      );
    },
    requiresCardDetails() {
      return (
        !!this.currentPaymentMethod &&
        !!this.currentPaymentMethod.card_processing
      );
    },
    totals() {
      return this._checkoutComposable.calculateTotals({
        subtotal: this.subtotal,
        tipAmount: this.tipAmount,
        totalTax: this.getTotalTax,
        paymentMethod: this.currentPaymentMethod
      });
    },
    baseTotal() {
      return this.totals.baseTotal;
    },
    convenienceTotal() {
      return this.totals.convenienceTotal;
    },
    grandTotal() {
      return this.totals.grandTotal;
    },
    total() {
      return this.grandTotal;
    },
    itemCount() {
      return (this.cartItems || []).reduce(
        (sum, item) => sum + Number(item.quantity || 0),
        0
      );
    },
    grossSubtotal() {
      // The stored subtotal is already discount-inclusive, so add the discount
      // back to show the pre-discount subtotal alongside a discount line.
      return Number(this.subtotal || 0) + Number(this.getDiscountTotal || 0);
    },
    deliveryAddress() {
      return this.getDeliveryAddress;
    },
    hasDeliveryAddressError() {
      return (
        this.orderConfig?.type === "delivery" &&
        (!this.deliveryAddress ||
          !this.deliveryAddress.address_1 ||
          this.deliveryAddress.address_1.trim() === "")
      );
    },
    isValid() {
      const baseValidation = this._checkoutComposable.getIsValid();
      if (this.orderConfig?.type === "delivery") {
        return baseValidation && !this.hasDeliveryAddressError;
      }
      return baseValidation;
    },
    fullPhoneNumber() {
      return this.selectedCountry.dial_code + this.details.phone;
    }
  },
  mounted() {
    window.scrollTo(0, 0);

    // Refresh-while-pending recovery: the online cart doesn't survive a reload,
    // so an empty cart plus a leftover idempotency key means a checkout was
    // in-flight when the page reloaded. Before bouncing to the menu, ask the
    // backend whether that attempt actually placed an order — if so, show the
    // success screen instead of letting the customer pay again.
    const pendingKey = peekIdempotencyKey();
    if (pendingKey && (!this.cartItems || this.cartItems.length === 0)) {
      this.reconcilePendingOrder(pendingKey);
      return;
    }

    if (!this.cartItems || this.cartItems.length === 0) {
      if (this.storeAlias) {
        this.$router.push({
          name: "OrderView",
          params: { storeAlias: this.storeAlias }
        });
      }
    }

    // Set default country code based on store country
    const storeCountry =
      this.$store.getters.storeCountry || this.getStore?.country;
    if (storeCountry) {
      this.selectedCountry = getCountryByCode(storeCountry);
    }

    this.loadCustomerInfoFromLocalStorage();
    this._checkoutComposable.loadSmsConsentFromStorage();
    this.loadDeliveryAddressFromLocalStorage();

    if (
      this.availablePaymentMethods &&
      this.availablePaymentMethods.length > 0
    ) {
      this.selectedPaymentType = this.availablePaymentMethods[0].id;
      this.paymentProcessor = this.availablePaymentMethods[0].payment_processor;
    }
  },
  watch: {
    selectedPaymentType: {
      handler(newValue) {
        const availablePaymentMethods = this.availablePaymentMethods;
        const paymentType = availablePaymentMethods.find(
          (paymentType) => paymentType.id === newValue
        );

        if (paymentType) {
          this.paymentProcessor = paymentType.payment_processor;
        }
      }
    },
    getDeliveryAddressWatcher: {
      handler() {
        this.clearDeliveryAddressErrorIfValid();
      },
      deep: true,
      immediate: true
    },
    "orderConfig.type": {
      handler(newType) {
        if (newType !== "delivery") {
          this.errors.deliveryAddress = "";
        }
      }
    },
    isScheduleModalOpen: {
      handler(isOpen) {
        if (!isOpen) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.clearDeliveryAddressErrorIfValid();
            }, 150);
          });
        }
      }
    },
    areTipsEnabled: {
      handler(enabled) {
        if (!enabled) {
          this.tipOption = 0;
          this.customTip = "";
        }
      },
      immediate: true
    }
  },
  methods: {
    getOrderTypeFromDiningOption(name) {
      const nameLower = (name || "").toLowerCase();
      if (
        nameLower.includes("pick") ||
        nameLower.includes("pickup") ||
        nameLower.includes("pick-up")
      ) {
        return "pickup";
      }
      if (nameLower.includes("curb")) {
        return "curbside";
      }
      if (nameLower.includes("deliver")) {
        return "delivery";
      }
      if (nameLower.includes("dine")) {
        return "dinein";
      }
      return "pickup";
    },

    formatScheduledTime(day, time) {
      if (!day || !time) return "";
      const dayLabel =
        day === "Today" ? "Today" : day === "Tomorrow" ? "Tomorrow" : day;
      return `${dayLabel} at ${time}`;
    },
    handleBack() {
      if (this.storeAlias) {
        this.$router.push({
          name: "OrderView",
          params: { storeAlias: this.storeAlias }
        });
      }
    },
    async reconcilePendingOrder(key) {
      // Ask the backend if the in-flight attempt for this key actually created an
      // order. If yes → the customer already paid and the order exists, so show
      // success (the cart is already empty after the reload). If no → the key is
      // stale; clear it and go to the menu. On a network error, keep the key so
      // the next load can try again.
      try {
        const order = await fetchOrderByIdempotencyKey(key);
        if (order && order.id) {
          clearIdempotencyKey();
          this.isSuccess = true;
          return;
        }
        clearIdempotencyKey();
      } catch (e) {
        console.warn("Order reconcile failed:", e);
      }
      if (this.storeAlias) {
        this.$router.push({
          name: "OrderView",
          params: { storeAlias: this.storeAlias }
        });
      }
    },
    saveCustomerInfoToLocalStorage() {
      // Store the full phone number with country code
      const customerToSave = {
        ...this.details,
        phone: this.fullPhoneNumber
      };
      saveCustomerInfo(customerToSave);
    },
    loadDeliveryAddressFromLocalStorage() {
      const savedAddress = loadDeliveryAddress();
      if (savedAddress && savedAddress.addressLine1) {
        const storeCountry =
          this.$store.getters.storeCountry ||
          this.$store.getters.getStore?.country ||
          "";
        const deliveryAddress = {
          address_1: savedAddress.addressLine1 || "",
          address_2: savedAddress.addressLine2 || "",
          city: savedAddress.city || "",
          state: savedAddress.state || "",
          postal_code: savedAddress.zip || "",
          country: storeCountry,
          first_name: "",
          last_name: "",
          phone: "",
          local_id: new Date().toISOString()
        };
        this.$store.dispatch("order/setDeliveryAddress", deliveryAddress);
      }
    },
    loadCustomerInfoFromLocalStorage() {
      const savedCustomerInfo = loadCustomerInfo();
      if (savedCustomerInfo) {
        this.details.firstName = savedCustomerInfo.firstName || "";
        this.details.lastName = savedCustomerInfo.lastName || "";
        this.details.email = savedCustomerInfo.email || "";

        // Parse stored phone to set country and digits
        const parsed = parsePhone(savedCustomerInfo.phone || "");
        this.selectedCountry = parsed.country;
        this.details.phone = parsed.digits;
      }
    },
    saveSmsConsentToLocalStorage() {
      this._checkoutComposable.saveSmsConsentToStorage();
    },
    handleFirstNameInput() {
      this.errors.firstName = "";
      this.saveCustomerInfoToLocalStorage();
    },
    handleLastNameInput() {
      this.errors.lastName = "";
      this.saveCustomerInfoToLocalStorage();
    },
    handleEmailInput() {
      this.errors.email = "";
      this.saveCustomerInfoToLocalStorage();
    },
    handlePhoneInput(value) {
      // Input is already cleaned by PhoneInput component
      this.details.phone = value;
      this.errors.phone = "";
      this.saveCustomerInfoToLocalStorage();
    },
    handleCountryChange(country) {
      this.selectedCountry = country;
      this.saveCustomerInfoToLocalStorage();
    },
    clearDeliveryAddressErrorIfValid() {
      const address = this.getDeliveryAddress;
      if (
        address &&
        address.address_1 &&
        typeof address.address_1 === "string" &&
        address.address_1.trim() !== ""
      ) {
        this.errors.deliveryAddress = "";
      }
    },
    validateForm() {
      this.errors = {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        scheduledTime: "",
        scheduledDay: "",
        cardDetails: {
          name: "",
          number: "",
          expiration: "",
          cvv: ""
        },
        deliveryAddress: ""
      };

      let hasErrors = false;

      if (!this.details.firstName || this.details.firstName.trim().length < 2) {
        this.errors.firstName = "This field is required (minimum 2 characters)";
        hasErrors = true;
      }

      if (!this.details.lastName || this.details.lastName.trim().length < 2) {
        this.errors.lastName = "This field is required (minimum 2 characters)";
        hasErrors = true;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!this.details.email || !emailRegex.test(this.details.email)) {
        this.errors.email = "A valid email address is required";
        hasErrors = true;
      }

      // Check digits length against country limit or default
      const limit = this.selectedCountry.limit || 10;
      if (!this.details.phone || this.details.phone.length < limit) {
        this.errors.phone = `A valid phone number is required (minimum ${limit} digits)`;
        hasErrors = true;
      }

      const isDeliverable = this.getDiningOptionObject?.is_deliverable;
      if (isDeliverable) {
        const address = this.getDeliveryAddress;
        if (!address || !address.address_1 || address.address_1.trim() === "") {
          this.errors.deliveryAddress = "Delivery address is required";
          hasErrors = true;
        } else {
          this.errors.deliveryAddress = "";
        }
      } else {
        this.errors.deliveryAddress = "";
      }

      // Validate scheduled time if timing is scheduled
      if (this.orderConfig?.timing === "scheduled") {
        if (
          !this.orderConfig?.scheduledTime ||
          this.orderConfig.scheduledTime.trim() === ""
        ) {
          this.errorMessage = "Please select a scheduled time for your order";
          this.errors.scheduledTime =
            "Please select a scheduled time for your order";
          hasErrors = true;
        } else {
          this.errors.scheduledTime = "";
        }
        if (
          !this.orderConfig?.scheduledDay ||
          this.orderConfig.scheduledDay.trim() === ""
        ) {
          this.errorMessage = "Please select a scheduled day for your order";
          this.errors.scheduledDay =
            "Please select a scheduled day for your order";
          hasErrors = true;
        } else {
          this.errors.scheduledDay = "";
        }
      } else {
        this.errors.scheduledTime = "";
        this.errors.scheduledDay = "";
      }

      // A closed or schedule-only store can't take ASAP orders. Require a
      // scheduled time and open the schedule picker popup so the customer can
      // choose one, instead of letting the order fail with a generic error.
      if (this._selectedStore) {
        const storeStatus = this._selectedStore.getStoreStatus();
        const mustSchedule =
          storeStatus === "schedule_only" || storeStatus === "closed";
        if (mustSchedule) {
          const hasScheduledTime =
            this.orderConfig?.timing === "scheduled" &&
            !!this.orderConfig?.scheduledTime &&
            this.orderConfig.scheduledTime.trim() !== "" &&
            !!this.orderConfig?.scheduledDay &&
            this.orderConfig.scheduledDay.trim() !== "";

          if (!hasScheduledTime) {
            this.errors.scheduledTime = "Please select a time for your order";
            this.errorMessage =
              storeStatus === "closed"
                ? "This location is currently closed. Please schedule your order for a later time."
                : "This location only accepts scheduled orders. Please choose a time.";
            // Open the schedule picker popup so they can pick a time.
            this.$store.dispatch("setScheduleModalOpen", {
              isOpen: true,
              options: { showAddress: false, showSchedule: true }
            });
            hasErrors = true;
          } else {
            this.errors.scheduledTime = "";
            this.errors.scheduledDay = "";
          }
        }
      }

      if (!this.currentPaymentMethod) {
        this.errorMessage = "Please select a payment method";
        hasErrors = true;
      }

      if (
        this.currentPaymentMethod &&
        this.currentPaymentMethod.card_processing
      ) {
        if (this.currentPaymentMethod.payment_processor === "Stripe") {
          if (
            !this.cardDetails.name ||
            this.cardDetails.name.trim().length < 3
          ) {
            this.errors.cardDetails.name = "Name on card is required";
            hasErrors = true;
          }
        } else if (
          this.currentPaymentMethod.payment_processor === "Dejavoo Online"
        ) {
          // Card data lives inside the Freedom to Design secure fields, which
          // validate themselves when the payment token is requested on submit.
        } else {
          if (
            !this.cardDetails.name ||
            this.cardDetails.name.trim().length < 3
          ) {
            this.errors.cardDetails.name = "Card holder is required";
            hasErrors = true;
          }
          if (
            !this.cardDetails.number ||
            this.cardDetails.number.replace(/\s+/g, "").length < 13
          ) {
            this.errors.cardDetails.number = "Card number is required";
            hasErrors = true;
          }
          if (
            !this.cardDetails.expiration ||
            this.cardDetails.expiration.length < 4
          ) {
            this.errors.cardDetails.expiration = "Expiration date is required";
            hasErrors = true;
          }
          if (!this.cardDetails.cvv || this.cardDetails.cvv.length < 3) {
            this.errors.cardDetails.cvv = "CVV is required";
            hasErrors = true;
          }
        }
      }

      // Scroll to first error
      if (hasErrors) {
        this.$nextTick(() => {
          // First, try to find input fields with errors
          const firstErrorElement = document.querySelector(".border-red-400");
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });
            if (
              firstErrorElement.tagName === "INPUT" &&
              firstErrorElement instanceof HTMLInputElement
            ) {
              firstErrorElement.focus();
            }
            return;
          }

          // Check for scheduled time/day errors
          if (this.errors.scheduledDay || this.errors.scheduledTime) {
            const scheduleSection = document.querySelector(
              "#delivery-schedule-section"
            );
            if (scheduleSection) {
              scheduleSection.scrollIntoView({
                behavior: "smooth",
                block: "center"
              });
              return;
            }
          }

          // Check for delivery address error
          if (this.errors.deliveryAddress) {
            const deliverySection = document.querySelector(
              "[data-delivery-section]"
            );
            if (deliverySection) {
              deliverySection.scrollIntoView({
                behavior: "smooth",
                block: "center"
              });
              return;
            }
          }
        });
      }

      return !hasErrors;
    },
    async handleSubmit() {
      if (this.isSubmitting || this.isStoreClosed) {
        return;
      }

      if (!this.validateForm()) {
        return;
      }

      const unavailableCartItems = this.getUnavailable86dCartItems();
      if (unavailableCartItems.length) {
        this.showUnavailableItemsModal(unavailableCartItems);
        return;
      }

      const stockLimitAdjustments = this.getStockLimitCartAdjustments();
      if (stockLimitAdjustments.length) {
        this.showStockLimitModal(stockLimitAdjustments);
        return;
      }

      this.isSubmitting = true;
      this.errorMessage = "";

      try {
        await this._checkoutComposable.submitCheckoutOrder({
          storeAlias: this.storeAlias,
          cartItems: this.cartItems,
          subtotal: this.subtotal,
          totalTax: this.getTotalTax,
          tipAmount: this.tipAmount,
          orderConfig: this.orderConfig,
          store: this.getStore,
          customerDetails: {
            ...this.details,
            phone: this.fullPhoneNumber
          },
          smsConsent: this.smsConsent,
          diningOptionName: this.getDiningOptionName,
          diningOptionObject: this.getDiningOptionObject,
          serveAt: this.getServeAt,
          deliveryAddress: this.getDeliveryAddress,
          paymentTypeId: this.selectedPaymentType,
          paymentMethod: this.currentPaymentMethod,
          cardDetails: this.cardDetails,
          stripeFormRef: this.$refs.paymentSection?.$refs?.stripeForm,
          dejavooFormRef: this.$refs.paymentSection?.$refs?.dejavooForm
        });

        this.isSubmitting = false;
        this.isSuccess = true;
      } catch (error) {
        if (await this.handleUnavailableItemsError(error)) {
          return;
        }
        if (await this.handleStockLimitError(error)) {
          return;
        }
        this._checkoutComposable.handleCheckoutError(error);
        this.showErrorToast(error);
      }
    },
    getStockLimitMessages(error) {
      const items = error?.data?.quantity_items;
      return Array.isArray(items) ? items : [];
    },
    async handleStockLimitError(error) {
      const stockLimitAdjustments = this.getStockLimitMessages(error);
      if (error?.status !== 400 || stockLimitAdjustments.length === 0) {
        return false;
      }

      this.isSubmitting = false;
      this.errorMessage = "";
      this.showStockLimitModal(stockLimitAdjustments);
      return true;
    },
    getUnavailableItemMessages(error) {
      const items = error?.data?.items;
      return Array.isArray(items) ? items : [];
    },
    getUnavailableModifierMessages(error) {
      const items = error?.data?.modifier_items;
      return Array.isArray(items) ? items : [];
    },
    getUnavailableItemNames(error) {
      return [
        ...this.getUnavailableItemMessages(error),
        ...this.getUnavailableModifierMessages(error)
      ]
        .map((message) =>
          String(message || "")
            .replace(/\s+is unavailable\.?$/i, "")
            .trim()
        )
        .filter(Boolean);
    },
    async handleUnavailableItemsError(error) {
      const unavailableCartItems = this.getUnavailable86dCartItems();
      const isGenericUnavailableError =
        error?.message ===
        "One or more items in your cart are no longer available.";
      const unavailableNames = this.getUnavailableItemNames(error);
      if (
        !isGenericUnavailableError &&
        (error?.status !== 400 || unavailableNames.length === 0)
      ) {
        return false;
      }

      const unavailableNameSet = new Set(
        unavailableNames.map((name) => name.toLowerCase())
      );
      const itemsToRemove = isGenericUnavailableError
        ? unavailableCartItems
        : (this.cartItems || []).filter(
            (item) =>
              unavailableNameSet.has(String(item.name || "").toLowerCase()) ||
              this.getCartItemModifierNames(item).some((name) =>
                unavailableNameSet.has(name.toLowerCase())
              )
          );

      this.isSubmitting = false;
      this.errorMessage = "";
      this.showUnavailableItemsModal(itemsToRemove, unavailableNames);
      return true;
    },
    showUnavailableItemsModal(itemsToRemove, fallbackNames = []) {
      const itemNames = itemsToRemove
        .map((item) => item.name || item.product?.name)
        .filter(Boolean);

      this.unavailableItemNames = itemNames.length ? itemNames : fallbackNames;
      this.unavailableItemsToRemove = itemsToRemove;
      this.unavailableItemsModalOpen = true;
    },
    getCartItemModifierNames(item) {
      const modifiers = item?.selectedModifiers || item?.modifiers || [];
      return modifiers
        .map((modifier) => String(modifier?.name || ""))
        .filter(Boolean);
    },
    async removeUnavailableItemsAndPlaceOrder() {
      const itemsToRemove = [...this.unavailableItemsToRemove];
      for (const item of itemsToRemove) {
        const productId = item.id || item.product?.id;
        if (productId) {
          this.$store.commit("store/SET_PRODUCT_86D", {
            productId,
            value: true
          });
        }
        if (item.cartId) {
          await this.$store.dispatch("order/removeItemCart", item.cartId);
        }
      }

      this.unavailableItemsModalOpen = false;
      this.unavailableItemNames = [];
      this.unavailableItemsToRemove = [];
      await this.handleSubmit();
    },
    goBackToShoppingFromUnavailableModal() {
      this.unavailableItemsModalOpen = false;
      this.handleBack();
    },
    showStockLimitModal(adjustments) {
      this.stockLimitAdjustments = adjustments.map((adjustment) => ({
        ...adjustment,
        requested_quantity: Number(adjustment.requested_quantity || 0),
        available_quantity: Math.max(
          Number(adjustment.available_quantity || 0),
          0
        ),
        removed_quantity: Math.max(Number(adjustment.removed_quantity || 0), 0)
      }));
      this.stockLimitModalOpen = true;
    },
    async applyStockLimitsAndPlaceOrder() {
      for (const adjustment of this.stockLimitAdjustments) {
        let remainingQuantity = Number(adjustment.available_quantity || 0);
        const matchingItems = (this.cartItems || []).filter(
          (item) =>
            String(item.id || item.product?.id) === String(adjustment.product)
        );

        for (const item of matchingItems) {
          if (remainingQuantity <= 0) {
            if (item.cartId) {
              await this.$store.dispatch("order/removeItemCart", item.cartId);
            }
            continue;
          }

          const currentQuantity = Number(item.quantity || 0);
          const nextQuantity = Math.min(currentQuantity, remainingQuantity);
          remainingQuantity -= nextQuantity;

          if (nextQuantity < currentQuantity && item.cartId) {
            await this.$store.dispatch("order/updateCartItem", {
              cartId: item.cartId,
              item,
              quantity: nextQuantity,
              note: item.note || "",
              modifiers: item.selectedModifiers || item.modifiers || []
            });
          }
        }
      }

      this.stockLimitModalOpen = false;
      this.stockLimitAdjustments = [];
      await this.handleSubmit();
    },
    goBackToShoppingFromStockLimitModal() {
      this.stockLimitModalOpen = false;
      this.handleBack();
    },
    formatQuantity(quantity) {
      const value = Number(quantity || 0);
      return Number.isInteger(value) ? String(value) : String(value);
    },
    getStockLimitCartAdjustments() {
      const productTotals = {};

      (this.cartItems || []).forEach((item) => {
        const productId = item.id || item.product?.id;
        const availableQuantity = Number(item.stock);
        if (
          !productId ||
          !item.dontAllowSaleIfNoStock ||
          Number.isNaN(availableQuantity)
        ) {
          return;
        }

        if (!productTotals[productId]) {
          productTotals[productId] = {
            product: productId,
            name: item.name || item.product?.name,
            requested_quantity: 0,
            available_quantity: Math.max(availableQuantity, 0)
          };
        }

        productTotals[productId].requested_quantity += Number(
          item.quantity || 0
        );
      });

      return Object.values(productTotals)
        .filter(
          (item) => item.requested_quantity > Number(item.available_quantity)
        )
        .map((item) => ({
          ...item,
          removed_quantity:
            Number(item.requested_quantity) - Number(item.available_quantity)
        }));
    },
    getUnavailable86dCartItems() {
      const isProduct86d = this.$store.getters["store/isProduct86d"];
      return (this.cartItems || []).filter((item) =>
        isProduct86d?.(item.id || item.product?.id)
      );
    },
    hasUnavailable86dItems() {
      return this.getUnavailable86dCartItems().length > 0;
    },
    showErrorToast(error) {
      let errorMessage = "Something went wrong. Please try again.";
      let errorTitle = "Error";

      if (error?.status === 500) {
        errorMessage = "Something went wrong. Please try again.";
        errorTitle = "Server Error";
      } else if (error?.status === 400) {
        if (error.data === "Credit card has expired.") {
          errorMessage =
            "Credit card has expired. Please use a different card.";
          errorTitle = "Payment Error";
          this.errors.cardDetails.number = "";
          this.errors.cardDetails.cvv = "";
          this.errors.cardDetails.expiration = "Credit card has expired.";
        } else if (
          error.data === "Invalid Card Number (3)" ||
          error.data?.includes("Card Number")
        ) {
          errorMessage = "Invalid card number. Please check and try again.";
          errorTitle = "Payment Error";
          this.errors.cardDetails.number = "Invalid card number";
          this.errors.cardDetails.cvv = "";
          this.errors.cardDetails.expiration = "";
        } else if (Array.isArray(error.data?.items)) {
          errorMessage = error.data.items.join(" ");
          errorTitle = "Unavailable item";
        } else if (error?.data) {
          errorMessage =
            typeof error.data === "string"
              ? error.data
              : error.data.error || "Something went wrong. Please try again.";
          errorTitle = "Error";
          if (
            this.currentPaymentMethod &&
            this.currentPaymentMethod.card_processing
          ) {
            this.errors.cardDetails.number = "Invalid";
            this.errors.cardDetails.cvv = "Invalid";
            this.errors.cardDetails.expiration = "Invalid";
          }
        } else {
          errorMessage = "Invalid card details. Please check and try again.";
          errorTitle = "Payment Error";
          if (
            this.currentPaymentMethod &&
            this.currentPaymentMethod.card_processing
          ) {
            this.errors.cardDetails.number = "Invalid";
            this.errors.cardDetails.cvv = "Invalid";
            this.errors.cardDetails.expiration = "Invalid";
          }
        }
      } else if (error?.isPaymentError && error?.message) {
        errorMessage = error.message;
        errorTitle = "Payment Error";
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.data) {
        errorMessage = error.data;
      }

      this.toastTitle = errorTitle;
      this.toastMessage = errorMessage;
      this.toastVariant = "danger";
      this.showToast = true;

      if (
        this.errors.cardDetails &&
        Object.values(this.errors.cardDetails).some((err) => err)
      ) {
        this.$nextTick(() => {
          const paymentSection = document.querySelector(
            "[data-payment-section]"
          );
          if (paymentSection) {
            paymentSection.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });
          }
        });
      }
    }
  }
};
</script>

<style scoped>
@media (min-width: 960px) {
  .checkout-main {
    max-width: 1120px;
  }

  .checkout-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(320px, 380px);
    column-gap: 2.5rem;
    row-gap: 2rem;
    align-items: start;
  }

  .checkout-grid > * + * {
    margin-top: 0 !important;
  }

  .checkout-contact {
    grid-column: 1;
    grid-row: 1;
  }

  .checkout-order {
    grid-column: 2;
    grid-row: 1;
  }

  .checkout-tip {
    grid-column: 2;
    grid-row: 2;
  }

  .checkout-payment {
    grid-column: 1;
    grid-row: 2 / span 2;
  }

  .checkout-summary {
    grid-column: 2;
    grid-row: 3;
    position: sticky;
    top: 1.5rem;
  }
}
</style>
